import {noop} from '@benchling/js-utils';
import PropTypes from 'prop-types';
import React from 'react';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  BioentityLinkFieldWithTagSchema,
  isRequiredLinkTagSchema,
  SCHEMA_FIELD_SHAPE,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import * as ModalContainer from 'ui-library/ModalContainer';

import CreateComponentModal from './CreateComponentModal';

const SELECT_NO_FIELD_DUMMY_OBJECT = {
  displayName: 'None',
};

// This is not an available option in the dropdown
const UNSELECTED_DUMMY_OBJECT = {
  displayName: 'Select an option',
};

interface ConfirmPayload {
  name: string;
  folderId: string;
  translationLink?: BioentityLinkFieldWithTagSchema | null;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}

type TranslationLinkFields = Array<{
  field: BioentityLinkFieldWithTagSchema;
  isSingleLinkFieldWithExistingValue: boolean;
}>;

interface CreateProteinModalProps {
  initialFolderId?: string;
  namePlaceholder?: string;
  onRequestClose: () => void;
  onConfirm: (confirmPayload: ConfirmPayload) => void;
  translationLinkFields?: TranslationLinkFields;
}

type CreateProteinModalOpenProps = Omit<CreateProteinModalProps, 'onRequestClose'>;

type TranslationLinkOrDummy =
  | SchemaField
  | typeof UNSELECTED_DUMMY_OBJECT
  | typeof SELECT_NO_FIELD_DUMMY_OBJECT;

function isRealTranslationLink(
  translationLinkOrDummy: TranslationLinkOrDummy
): translationLinkOrDummy is BioentityLinkFieldWithTagSchema {
  return ![UNSELECTED_DUMMY_OBJECT, SELECT_NO_FIELD_DUMMY_OBJECT].includes(translationLinkOrDummy);
}

interface CreateProteinModalState {
  tagSchemaId: string | null;
  translationLink: TranslationLinkOrDummy;
  selectedStudyItems: Array<StudySearchItem>;
}

class CreateProteinModal extends React.Component<CreateProteinModalProps, CreateProteinModalState> {
  static propTypes = {
    initialFolderId: PropTypes.string,
    namePlaceholder: PropTypes.string,
    onRequestClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    translationLinkFields: PropTypes.arrayOf(
      PropTypes.shape({
        field: SCHEMA_FIELD_SHAPE.isRequired,
        isSingleLinkFieldWithExistingValue: PropTypes.bool.isRequired,
      }).isRequired
    ),
  };

  static open(props: CreateProteinModalOpenProps): void {
    ModalContainer.open(
      CreateProteinModal,
      <CreateProteinModal
        {...props}
        onRequestClose={() => {
          ModalContainer.close(CreateProteinModal);
        }}
      />
    );
  }

  static defaultProps = {
    translationLinkFields: [],
  };

  override state: CreateProteinModalState = {
    tagSchemaId: null,
    translationLink: UNSELECTED_DUMMY_OBJECT,
    selectedStudyItems: [],
  };

  _hasCompatibleTranslationLinkOptions = (): boolean =>
    this.props.translationLinkFields!.some(
      (translationLinkField) =>
        this.state.tagSchemaId == null ||
        // @ts-expect-error Assumes no batch schemas
        translationLinkField.field.requiredLink?.tagSchema.id === this.state.tagSchemaId
    );

  _getTranslationLinkOptions = (): Array<{
    key: number | string;
    name: string;
    handleOnClick: () => void;
    disabled?: boolean;
  }> => [
    {
      key: 'None',
      name: 'None',
      handleOnClick: () => {
        this._handleChangeTranslationLink(SELECT_NO_FIELD_DUMMY_OBJECT);
      },
    },
    ...this.props
      .translationLinkFields!.filter(
        (translationLinkField) =>
          this.state.tagSchemaId == null ||
          // @ts-expect-error Assumes no batch schemas
          translationLinkField.field.requiredLink?.tagSchema.id === this.state.tagSchemaId
      )
      .map(({field, isSingleLinkFieldWithExistingValue}) => {
        if (isSingleLinkFieldWithExistingValue) {
          return {
            key: field.id,
            disabled: true,
            name: `${field.displayName} (Already specified))`,
            handleOnClick: noop,
          };
        } else {
          return {
            key: field.id,
            name: field.displayName,
            handleOnClick: () => {
              this._handleChangeTranslationLink(field);
            },
          };
        }
      }),
  ];

  _handleChangeTranslationLink = (field: TranslationLinkOrDummy): void => {
    const stateUpdateObject: Partial<CreateProteinModalState> = {translationLink: field};
    if (isRealTranslationLink(field)) {
      // @ts-expect-error Assumes no batch schemas
      stateUpdateObject.tagSchemaId = field.requiredLink?.tagSchema.id;
    }
    // @ts-expect-error
    this.setState(stateUpdateObject);
  };

  _handleConfirm = ({folderId, name}: {folderId: string; name: string}): void => {
    const translationLink: BioentityLinkFieldWithTagSchema | null = !isRealTranslationLink(
      this.state.translationLink
    )
      ? null
      : this.state.translationLink;
    const confirmPayload: ConfirmPayload = {folderId, name, translationLink};
    if (translationLink == null && this.state.tagSchemaId != null) {
      confirmPayload.tagSchemaId = this.state.tagSchemaId;
    }
    if (this.state.selectedStudyItems.length > 0) {
      confirmPayload.studyIds = this.state.selectedStudyItems.map((studyItem) => studyItem.id);
    }
    this.props.onConfirm(confirmPayload);
    this.props.onRequestClose();
  };

  override render(): JSX.Element {
    return (
      <CreateComponentModal
        additionalSubmitDisabled={
          this._hasCompatibleTranslationLinkOptions() &&
          this.state.translationLink === UNSELECTED_DUMMY_OBJECT
        }
        confirmButtonText="Create"
        initialFolderId={this.props.initialFolderId}
        readableItemType="entity"
        namePlaceholder={this.props.namePlaceholder}
        onConfirm={this._handleConfirm}
        onRequestClose={this.props.onRequestClose}
        title="Create AA sequence"
      >
        {this.props.translationLinkFields!.length > 0 && (
          <FormGroup>
            <FieldLabel label="Translation link" />
            <SelectDropdown
              disabled={!this._hasCompatibleTranslationLinkOptions()}
              displayName={
                this._hasCompatibleTranslationLinkOptions()
                  ? this.state.translationLink.displayName
                  : 'No compatible fields available'
              }
              menu={
                <Menu width={MenuWidth.FULL}>
                  {this._getTranslationLinkOptions().map(({key, name, handleOnClick, disabled}) => (
                    <Option key={key} isDisabled={disabled} onSelect={handleOnClick}>
                      {name}
                    </Option>
                  ))}
                </Menu>
              }
            />
          </FormGroup>
        )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_SET_SCHEMA}) && (
          <FormGroup>
            <FieldLabel label="Schema" />
            <TagSchemaSelector
              allowNone={true}
              isDisabled={
                isRealTranslationLink(this.state.translationLink) ||
                // @ts-expect-error This is a SchemaField, but we're passing it to this fn for some reason
                isRequiredLinkTagSchema(this.state.translationLink)
              }
              oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
              selectedTagSchemaId={this.state.tagSchemaId}
              tagSchemaFilterFn={(schema) =>
                // @ts-expect-error This is a SchemaField, but we're passing it to this fn for some reason
                !isRequiredLinkTagSchema(this.state.translationLink) ||
                // @ts-expect-error isRequiredLinkTagSchema should always fail :|
                this.state.translationLink.requiredLink.tagSchema.id === schema.id
              }
              onChange={(tagSchema) => {
                this.setState({tagSchemaId: tagSchema ? tagSchema.id : null});
              }}
            />
          </FormGroup>
        )}
        {BenchlingConfig.STUDIES_ENABLE && (
          <FormGroup>
            <FieldLabel label="Studies" />
            <StudySearchInput
              selectedItems={this.state.selectedStudyItems}
              shouldAllowMultiple={true}
              onChange={(studySearchItems) => {
                this.setState({selectedStudyItems: studySearchItems});
              }}
            />
          </FormGroup>
        )}
      </CreateComponentModal>
    );
  }
}

export default CreateProteinModal;
